import React from "react";
import { Link, Text } from "@jsq/ds/core";
import * as styles from "./styles_vanilla.css";
import type { Components } from "react-markdown";

const ALLOWED_PROTOCOLS = ["http:", "https:", "mailto:"];

// A base is only needed so `URL` can resolve a protocol-relative reference
// (e.g. `//host/path`) to check its protocol — we always return the
// original `url` string, never `parsed.href`, so the base's own value never
// reaches the rendered output. A fixed placeholder (rather than
// `window.location.origin`) keeps this working in any SSR environment,
// where `window` doesn't exist.
const URL_BASE = "https://sanitize-url.invalid";

const sanitizeUrl = (url: string): string => {
  try {
    const parsed = new URL(url, URL_BASE);
    if (ALLOWED_PROTOCOLS.includes(parsed.protocol)) {
      return url;
    }
    return "#";
  } catch {
    return "#";
  }
};

// Renders react-markdown output through `@jsq/ds` `Text` (and sanitizes link
// hrefs against `ALLOWED_PROTOCOLS`) instead of default HTML elements. Fay
// summaries are LLM-generated, so the allowlist matters even though this
// package's other markdown consumers don't need one.
export const richMarkdownComponents: Components = {
  h1: ({ children }) => (
    <Text variant="h1" className={styles.markdownHeading}>
      {children}
    </Text>
  ),
  h2: ({ children }) => (
    <Text variant="h2" className={styles.markdownHeading}>
      {children}
    </Text>
  ),
  h3: ({ children }) => (
    <Text variant="h3" className={styles.markdownHeading}>
      {children}
    </Text>
  ),
  h4: ({ children }) => (
    <Text variant="h4" className={styles.markdownHeading}>
      {children}
    </Text>
  ),
  h5: ({ children }) => (
    <Text variant="strong-md" className={styles.markdownHeading}>
      {children}
    </Text>
  ),
  h6: ({ children }) => (
    <Text variant="strong-sm" className={styles.markdownHeading}>
      {children}
    </Text>
  ),
  p: ({ children }) => (
    <Text variant="regular-md" className={styles.markdownParagraph}>
      {children}
    </Text>
  ),
  ul: ({ children }) => <ul className={styles.markdownList}>{children}</ul>,
  // `start` carries a markdown list's actual starting number (e.g. `3. foo`)
  // and must be forwarded, or a list that doesn't start at 1 renders as if
  // it did.
  ol: ({ children, start }) => (
    <ol className={styles.markdownList} start={start}>
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className={styles.markdownListItem}>
      <Text variant="regular-md">{children}</Text>
    </li>
  ),
  blockquote: ({ children }) => (
    <blockquote className={styles.markdownBlockquote}>{children}</blockquote>
  ),
  // Fenced code (labelled or not) is always wrapped in a `pre` by
  // remark-rehype; only inline code spans render a bare `code` — so the
  // block wrapper belongs on `pre`, not on an `isInline = !className` check
  // inside `code` (react-markdown v9 dropped the `inline` prop that used to
  // make that check reliable, and a labelled block wrapped there produces a
  // second, nested `pre`). A language-less fenced block still carries no
  // `className` though, so `code` falls back to a multiline check to tell
  // it apart from a true single-line inline span.
  pre: ({ children }) => <pre className={styles.markdownCodeBlock}>{children}</pre>,
  code: ({ children, className }) => {
    const isBlock =
      Boolean(className) || (typeof children === "string" && children.includes("\n"));
    if (isBlock) {
      return <code className={className}>{children}</code>;
    }
    return <code className={styles.markdownCode}>{children}</code>;
  },
  a: ({ children, href }: { children?: React.ReactNode; href?: string }) => {
    if (href?.startsWith("mailto:")) {
      return <>{children}</>;
    }

    const sanitizedHref = href ? sanitizeUrl(href) : "#";
    return (
      <Link href={sanitizedHref} shouldOpenInNewTab isInline className={styles.markdownLink}>
        {children}
      </Link>
    );
  },
};
